import React, { useState, useEffect, useRef } from "react";
import "../navbar.css";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import LoginModal from "./LoginModal";
import LoginPaid from "./LoginPaid";
import SignUp from "./SignUp";
import { useAuth } from "./AuthContext";
import LoginPaidForSignup from "./LoginPaidForSignup";
import SkipLink from "../a11y/SkipLink";
import { OPEN_LOGIN_EVENT } from "./ui/Locked";
import { navCurrent } from "./navMatch";

// The site navbar (foundation, Nav.dc.html / NavPhoneOpen.dc.html): 64px, fixed at the top.
// Sentence-case links; the current page is blue with a 2px underline. Under 1180px the links fold
// into a 44px menu button that opens a full-width sheet of 48px rows.
// A link also stays marked on the pages under it (`also`): Portfolios on a portfolio's details,
// Research Lab on Factor School, Trade Ideas on a trade idea, Pricing on checkout (Checkout.dc.html).
// header.navbar is read by BossAI (its sticky parts sit under it): keep that class.
const LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/daSignal", label: "DA Signal" },
  { to: "/portfolios", label: "Portfolios", also: ["/portfolioDetails"] },
  { to: "/research", label: "Research Lab", also: ["/factorSchool", "/factorInfo"] },
  { to: "/tradeIdeas", label: "Trade Ideas", also: ["/tradeDetailView"] },
  { to: "/bossai", label: "BossAI" },
  // owner, 13 Oct 2026: "show backtester as well on NAV like BOSSAI" - the newer backtester (templates, NAV,
  // live search); the older /backtester pages still mark it
  { to: "/backtester-new", label: "Backtester", also: ["/backtester", "/backtesterDetails"] },
  { to: "/blogs", label: "Blogs" },
  { to: "/pricing", label: "Pricing", also: ["/checkout", "/kycVerification"] },
];

const MenuIcon = ({ open }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" aria-hidden="true" focusable="false">
    {open ? (
      <>
        <path d="M6 6l12 12" />
        <path d="M18 6L6 18" />
      </>
    ) : (
      <>
        <path d="M4 7h16" />
        <path d="M4 12h16" />
        <path d="M4 17h16" />
      </>
    )}
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </svg>
);

const Navbar = ({ openLogin, isLoginOpen, setIsLoginOpen }) => {
  const { isLoggedIn } = useAuth();
  const [internalModal, setInternalModal] = useState(false);

  const showModal = isLoginOpen ?? internalModal;
  const setShowModal = setIsLoginOpen ?? setInternalModal;
  const [showOnboard, setShowOnboard] = useState(false);
  const [showPaid, setShowPaid] = useState(false);
  const [showSignupOTP, setShowSignupOTP] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef(null);
  const sheetRef = useRef(null);

  // "Sign in to see" (ui/Locked) anywhere on the page opens the sign-in dialog
  useEffect(() => {
    const open = () => setShowModal(true);
    window.addEventListener(OPEN_LOGIN_EVENT, open);
    return () => window.removeEventListener(OPEN_LOGIN_EVENT, open);
  }, [setShowModal]);

  // The light/dark switch is gone (the site is light only): drop a class an old visit left behind.
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  // A new page closes the menu
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Escape closes the menu (keyboard users); focus that was in the menu goes back to the menu button
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const inSheet = sheetRef.current && sheetRef.current.contains(document.activeElement);
      setMenuOpen(false);
      if (inSheet && menuBtnRef.current) menuBtnRef.current.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const openSignIn = () => {
    // From the phone menu sheet: the sheet (and its button that opened the dialog) is about to go, so
    // the menu button takes focus first; the dialog keeps it as its opener and returns focus there.
    if (sheetRef.current && sheetRef.current.contains(document.activeElement) && menuBtnRef.current) {
      menuBtnRef.current.focus();
    }
    setMenuOpen(false);
    setShowModal(true);
  };

  return (
    <>
      <SkipLink />
      <header className="navbar fn-nav">
        <div className="fn-nav-in">
          <NavLink className="fn-nav-brand" to="/" aria-label="BossInvestor home">
            <img src="/bossinvestorLogo.png" alt="" className="fn-nav-logo" />
            <span className="fn-nav-brandtext">
              <span className="fn-nav-name">BossInvestor</span>
              <span className="fn-nav-tag">Systematic equity research</span>
            </span>
          </NavLink>

          <nav aria-label="Main" className="fn-nav-links">
            {LINKS.map((l) => {
              const cur = navCurrent(l, location.pathname);
              return (
                <Link key={l.to} to={l.to} className={cur ? "fn-nav-link is-active" : "fn-nav-link"} aria-current={cur || undefined}>
                  {l.label}
                </Link>
              );
            })}
          </nav>

          <div className="fn-nav-right">
            {!isLoggedIn ? (
              <button type="button" className="fn-btn fn-btn-secondary fn-btn-sm" onClick={openSignIn}>
                Log in
              </button>
            ) : (
              <NavLink to="/profile" className="fn-btn fn-btn-secondary fn-btn-sm fn-nav-profile">
                <UserIcon />
                <span className="fn-nav-profile-label">Your profile</span>
              </NavLink>
            )}
            <button
              type="button"
              ref={menuBtnRef}
              className="fn-nav-menu"
              aria-label="Menu"
              aria-expanded={menuOpen}
              aria-controls="fn-nav-sheet"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <MenuIcon open={menuOpen} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <>
            <div className="fn-nav-backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true" />
            <nav id="fn-nav-sheet" aria-label="Main" className="fn-nav-sheet" ref={sheetRef}>
              {LINKS.map((l) => {
                const cur = navCurrent(l, location.pathname);
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    className={cur ? "fn-nav-row is-active" : "fn-nav-row"}
                    aria-current={cur || undefined}
                    onClick={() => setMenuOpen(false)}
                  >
                    {l.label}
                  </Link>
                );
              })}
              {!isLoggedIn ? (
                <button type="button" className="fn-btn fn-btn-primary fn-btn-block fn-nav-sheet-cta" onClick={openSignIn}>
                  Log in or create a free account
                </button>
              ) : (
                <button
                  type="button"
                  className="fn-btn fn-btn-secondary fn-btn-block fn-nav-sheet-cta"
                  onClick={() => navigate("/profile")}
                >
                  Your profile
                </button>
              )}
            </nav>
          </>
        )}
      </header>

      <LoginModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onLogin={(type) => {
          setShowModal(false);
          if (type === "paid") {
            setShowPaid(true); // sign in with mobile + OTP
          } else if (type === "freeSignup") {
            setShowSignupOTP(true); // verify the mobile, then register
          } else {
            setShowOnboard(true);
          }
        }}
      />

      <LoginPaid
        isOpen={showPaid}
        onClose={() => setShowPaid(false)}
        onLoginSuccess={() => {
          setShowPaid(false);
        }}
        onNewUser={(phone) => {
          setShowPaid(false);
          setVerifiedPhone(phone);
          setShowOnboard(true); // first-time user -> registration screen
        }}
      />

      <SignUp isOpen={showOnboard} onClose={() => setShowOnboard(false)} phoneNumber={verifiedPhone} />

      <LoginPaidForSignup
        isOpen={showSignupOTP}
        onClose={() => setShowSignupOTP(false)}
        onVerifySuccess={(phoneNumber) => {
          setShowSignupOTP(false);
          setVerifiedPhone(phoneNumber);
          setShowOnboard(true); // open signup modal
        }}
      />
    </>
  );
};

export default Navbar;
